import { useEffect } from 'react';
import "./SettingsPreview.scss";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import { setToast } from "../../../../app/appSlice";
import LabelWithInfo from "../../../../components/labels/LabelWithInfo";
import JsonEditorDialog from "../../../../components/code/JsonEditorDialog";
import {
  changeSidebarPane,
  PipelineState,
  setEnvironnement,
  setLabels,
  setTopologyCPULimit,
  setTopologyCPURequest,
  setTopologyDescription,
  setTopologyMemoryLimit,
  setTopologyMemoryRequest,
  setTopologyMessageStore,
  setTopologyModeBatch,
  setTopologyName,
  setTopologyParallelism,
  setTopologyRetentionTime,
  setTopologyRuntimeVersion,
} from "../../editorSlice";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { RuntimeMode } from "../../../../app/apiSlice";
import { Button } from "primereact/button";
import CronPlanningPreview from "./Cron/planning/CronPlanningPreview";
import { DropDownEnv } from "../../../../components/environnement/DropDownEnv";
import { useDefaultsKubernetesValuesConvertor } from "../../../../hooks/useDefaultsKubernetesValuesConvertor";
import { InputSwitch } from "primereact/inputswitch";
import { LabelsSelectors, useLabelsSelectors } from "@kosmos/shared";
import { InputNumberSlider } from './InputNumberSlider';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCogs } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'primereact/tooltip';
import { setChangedTopology, setTopologyGlobalConfigAdditions } from '../../editorSlice';
import {
  useTopologyConfigurationValidator,
} from "../../../../hooks/useTopologyConfigurationValidator";

export const SettingsPreview = ({ id }: { id: string }) => {
  const { t } = useTranslation();
  const topology = useAppSelector((state) => state.editor.current);
  const dispatch = useAppDispatch();
  const {cpuMax, memoryMax , memoryMin, cpuMin} = useDefaultsKubernetesValuesConvertor();
  const environnements = useAppSelector((state) => state.app.config.environnements);
  const { selectedLabels, validate: validateLabels } = useLabelsSelectors();
  const pipelineState = useAppSelector((state) =>  state.editor.pipelineState);
  const { checkIfYOutputArePresent } = useTopologyConfigurationValidator();
  useEffect(() => {
    if (validateLabels()) {
      if (selectedLabels && selectedLabels != topology?.metadatas) {
        dispatch(setLabels(selectedLabels));
      }
    }
  }, [selectedLabels])
  const isPipelineRunning = (): boolean => {
    return pipelineState !== PipelineState.STOPPED && pipelineState !== PipelineState.DELETED && pipelineState !== PipelineState.COMPLETED && pipelineState !== PipelineState.FAILED;
  }
  const [showDialog, setShowDialog] = useState(false);
  return (
    <div className="settings-preview p-4">
      <Tooltip
        target=".advanced-config-btn"
        content="Configuration avancée"
        position="top"
        className="global-tooltip"
      />

      <div className="absolute top-0 left-0 m-2 z-50">
        <button
          className="advanced-config-btn bg-transparent border-none cursor-pointer"
          onClick={() => setShowDialog(true)}
        >
          <FontAwesomeIcon icon={faCogs} size="lg" />
        </button>
      </div>
      <div className="grid p-fluid">

        <div className="col-12 p-field">
          <div className="p-label">
            <b>
              <label>Titre</label>
            </b>
            <br />
            <InputText
              value={topology?.name}
              disabled={isPipelineRunning()}
              onChange={(e) => {
                const newValue = e.target.value;

                if (newValue.length > 128) {
                  dispatch(
                    setToast({
                      title: "Limite atteinte",
                      content: "Le titre ne peut pas dépasser 128 caractères.",
                      severity: "error",
                      life: 4000,
                    })
                  );
                  return;
                }

                dispatch(setTopologyName(newValue));
              }}
            />
          </div>
        </div>

        <div className="col-12 p-field">
          <div className="p-label">
            <b>
              <label>Description</label>
            </b>
            <br />
            <InputTextarea
              value={topology?.description}
              disabled={isPipelineRunning()}
              onChange={(e) => {
                const newValue = e.target.value;

                if (newValue.length > 128) {
                  dispatch(
                    setToast({
                      title: "Limite atteinte",
                      content: "La description ne peut pas dépasser 128 caractères.",
                      severity: "error",
                      life: 4000,
                    })
                  );
                  return;
                }

                dispatch(setTopologyDescription(newValue));
              }}
            />
          </div>
        </div>

        {topology?.runtimeMode !== RuntimeMode.ANALYTIC && (
          <div className="col-12 p-field">
          <div className="p-label">
            <b>
              <label>Rétention des données (en heures)</label>
            </b>
            <br />
            <InputNumber
              min={0}
              value={topology && topology.retentionTimeNats ? Number(topology.retentionTimeNats.replace(/[^0-9]/g, '')) : null}
              disabled={isPipelineRunning()}
              onChange={(e) => {
                const newValue: string = e.value !== null ? e.value + 'h' : "";
                dispatch(setTopologyRetentionTime(newValue));
              }}
            ></InputNumber>
          </div>
        </div>
        )}

        {topology?.runtimeMode === RuntimeMode.BASIC && (
          <>
          <div className="col-12 p-field">
            <div className="p-label">
              <b>
                <label>{t("label.batch")}</label>
              </b>
              <br />
              <InputSwitch
                disabled={isPipelineRunning()}
                onChange={(e: any) => {
                  const newValue = e.value;
                  if (checkIfYOutputArePresent()) {
                    dispatch(
                      setToast({
                        title: "Lien impossible",
                        content: t("error.yinbatch"),
                        severity: "error",
                        life: 5000,
                      })
                    );
                    return;
                  }
                  dispatch(setTopologyModeBatch(newValue));
                }}
                checked={topology?.modeBatch}
              />
            </div>
          </div>
          <div className="col-12 p-field">
              <div className="p-label">
                <LabelWithInfo
                  label={t("label.messageStore")}
                  helperText={t("label.messageStoreHelper")}
                />
                <InputSwitch
                    disabled={isPipelineRunning()}
                    onChange={(e: any) => {
                      dispatch(setTopologyMessageStore(e.value));
                    }}
                    checked={topology?.messageStore}
                    style={{ marginTop: '0rem' }}
                />
              </div>
            </div>
          </>
        )}

        {topology?.runtimeMode === RuntimeMode.ANALYTIC && (
          <>
            <div className="col-12 p-field">
              <div className="p-label">
                <b>
                  <label>{t("label.parallelism")}</label>
                </b>
                <br />
                <InputNumber
                  value={topology?.parallelism}
                  disabled={isPipelineRunning()}
                  onChange={(e) => {
                    if (e.value) dispatch(setTopologyParallelism(e.value));
                  }}
                ></InputNumber>
              </div>
            </div>

            <div className="col-12 p-field">
              <InputNumberSlider min={memoryMin} max={memoryMax} data={topology.memoryRequest} disabled={isPipelineRunning()}
                                 label={t("label.memoryRequest")} update={setTopologyMemoryRequest} />
            </div>
            <div className="col-12 p-field mb-2">
              <InputNumberSlider min={memoryMin} max={memoryMax} data={topology.memoryLimit} disabled={isPipelineRunning()}
                                 label={t("label.memoryLimit")} update={setTopologyMemoryLimit} />
            </div>
            <div className="col-12 p-field">
              <InputNumberSlider min={cpuMin} max={cpuMax} data={topology.cpuRequest} disabled={isPipelineRunning()}
                                 step={0.1} minFractionDigits={2} factor={1000}
                                 label={t("label.cpuRequest")} update={setTopologyCPURequest} />
            </div>
            <div className="col-12 p-field mb-2">
              <InputNumberSlider min={cpuMin} max={cpuMax} data={topology.cpuLimit} disabled={isPipelineRunning()}
                                 step={0.1} minFractionDigits={2} factor={1000}
                                 label={t("label.cpuLimit")} update={setTopologyCPULimit} />
            </div>
          </>
        )}

        {environnements && (
          <div className="col-12  p-field">
            <span className="p-label">
              <label style={{ fontWeight: "bold" }}>Environnement cible</label>
              <DropDownEnv
                disabled={isPipelineRunning()}
                envSelected={environnements.find(
                  (env) => env.code === topology?.environnement
                )}
                setSelectedEnv={(env) => dispatch(setEnvironnement(env.code))}
              />
            </span>
          </div>
        )}

        <div className="col-12  p-field">
          <LabelsSelectors values={topology?.metadatas} displayGrid={false} readonly={isPipelineRunning()} />
        </div>

        {topology?.runtimeMode === RuntimeMode.ANALYTIC && (
          <>
            <div className="col-12 p-field mb-2">
              <b>
                <label>{t("label.runtimeVersion")}</label>
              </b>
              <br />
              <InputText
                value={topology?.runtimeVersion}
                disabled={isPipelineRunning()}
                onChange={(e) => {
                  dispatch(setTopologyRuntimeVersion(e.target.value));
                }}
              ></InputText>
            </div>
          </>
        )}

        {(topology?.runtimeMode === RuntimeMode.ANALYTIC || topology?.runtimeMode === RuntimeMode.BASIC && topology.modeBatch) && (
          <div className="col-12 p-field mb-2">
            <section className="panels__cron-tab">
              <div className="cron-tab__edit">
                <span className="font-bold">{t("cron-tab.title")}</span>
                <Button
                  type="button"
                  label="Editer"
                  disabled={isPipelineRunning()}
                  className="p-button-text"
                  onClick={() => {
                    dispatch(changeSidebarPane({ pane: "cron", arg: id }));
                  }}
                />
              </div>
              <div className="cron-tab__config">
                {!topology?.schedule && (
                  <span className="p-text-secondary">
                    {t("cron-tab.no_data")}
                  </span>
                )}
                {topology?.schedule && (
                  <CronPlanningPreview
                    cron={topology?.schedule}
                    nextExecutionDates={2}
                  />
                )}
              </div>
            </section>
          </div>
        )}
      </div>
      {/* Composant Dialog JSON */}
      {showDialog && (
        <JsonEditorDialog
          visible={showDialog}
          onClose={() => setShowDialog(false)}
          initialValue={topology?.globalConfigAdditions}
          title={t("editor.globalconftitle")}
          onSave={(newValue) => {
            dispatch(setTopologyGlobalConfigAdditions(newValue));
            dispatch(setChangedTopology(true));
          }}
        />
      )}
    </div>
  );
};
